import { useState, useEffect } from "react";
import ActionBar from "@/components/ActionBar";
import { useOffline } from "@/context/OfflineContext";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDate } from "@/lib/api";
import { hasPin, setPin as savePin, clearPin, pinScope } from "@/lib/pin";
import Modal from "@/components/Modal";
import { toast } from "sonner";
import { Wifi, WifiOff, RefreshCw, AlertTriangle, CheckCircle2, Clock, KeyRound, ChevronDown } from "lucide-react";

export default function SyncStatus({ actions = null }) {
  const { online, lastSync, queue, pendingCount, errorCount, syncing, flush, retryOp, clearSynced } = useOffline();
  const { user } = useAuth();
  const scope = pinScope(user);
  const [openList, setOpenList] = useState(false);
  const [pinModal, setPinModal] = useState(false);
  const [pinVal, setPinVal] = useState("");
  const [pinSet, setPinSet] = useState(false);
  useEffect(() => { setPinSet(hasPin(scope)); }, [scope]);

  const pending = queue.filter((o) => o.status === "pending");
  const errors = queue.filter((o) => o.status === "error");
  const synced = queue.filter((o) => o.status === "synced");

  const savePinNow = async () => {
    if (!scope) return toast.error("Connectez-vous pour définir un code PIN.");
    if (!/^\d{4,8}$/.test(pinVal)) return toast.error("Le PIN doit comporter 4 à 8 chiffres.");
    await savePin(pinVal, scope); setPinSet(true); setPinModal(false); setPinVal("");
    toast.success("Code PIN hors-ligne enregistré sur cet appareil.");
  };
  const removePin = () => { clearPin(scope); setPinSet(false); setPinModal(false); setPinVal(""); toast.success("Code PIN supprimé."); };

  // La bannière soignée s'affiche uniquement quand elle est utile.
  const alert = !online || pendingCount > 0 || errorCount > 0;

  // Barre d'actions (mode caisse) : raccourcis vers les modules autorisés de l'utilisateur.
  // « Vendre » (POS) apparaît actif sur la caisse et sert de retour direct au POS.
  const hasActions = Array.isArray(actions) && actions.length > 0;
  const ActionRow = ({ dark = false }) => !hasActions ? null : <ActionBar actions={actions} dark={dark} />;

  const PinModal = (
    <Modal open={pinModal} onClose={() => setPinModal(false)} title="Code PIN hors-ligne" testId="pin-setup-modal"
      footer={<>
        <button className="btn-outline" onClick={() => setPinModal(false)}>Annuler</button>
        {pinSet && <button className="btn-danger" onClick={removePin} data-testid="pin-remove-btn">Supprimer</button>}
        <button className="btn-primary" onClick={savePinNow} data-testid="pin-save-btn">{pinSet ? "Modifier" : "Enregistrer"}</button>
      </>}>
      <div className="space-y-3">
        <p className="text-sm text-[#64748B]">Définissez un code (4 à 8 chiffres) pour déverrouiller l'application sur cet appareil lorsque vous êtes hors connexion. Il est stocké de façon sécurisée et localement, jamais envoyé au serveur.</p>
        <input type="password" inputMode="numeric" className="inp text-center tracking-widest" value={pinVal} onChange={(e) => setPinVal(e.target.value)} placeholder="••••" data-testid="pin-setup-input" />
      </div>
    </Modal>
  );

  // ── État calme : barre fine et sobre ─────────────────────────────
  if (!alert) {
    return (
      <div className="border-b border-[#EEF2F7] bg-white text-xs" data-testid="sync-status-bar">
        <div className="px-4 md:px-6 min-h-9 py-1.5 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#DCFCE7] text-[#15803D] shrink-0" data-testid="sync-online-state" title="En ligne">
            <Wifi className="w-3.5 h-3.5" />
          </span>
          <span className="text-[#94A3B8] whitespace-nowrap shrink-0">Synchronisé <span data-testid="sync-last" className="text-[#64748B] font-medium">{lastSync ? fmtDate(lastSync) : "à l'instant"}</span></span>
          <span className="hidden" data-testid="sync-pending-count">0</span>
          {hasActions && <div className="hidden md:block flex-1 min-w-0"><ActionRow /></div>}
          <button
            className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold transition-colors shrink-0 ${pinSet ? "text-[#15803D] hover:bg-[#DCFCE7]" : "text-[#64748B] hover:bg-slate-100"}`}
            onClick={() => setPinModal(true)} data-testid="pin-setup-btn" title="Code PIN hors-ligne">
            <KeyRound className="w-3.5 h-3.5" /> {pinSet ? "PIN actif" : "Code PIN"}
          </button>
        </div>
        {hasActions && <div className="md:hidden px-4 pb-2" data-testid="pos-action-bar-mobile"><ActionRow /></div>}
        {PinModal}
      </div>
    );
  }

  // ── Bannière soignée : hors-ligne ou opérations en attente ───────
  const title = !online ? "Vous êtes hors connexion" : errorCount > 0 ? "Synchronisation incomplète" : "Synchronisation en attente";
  const subtitle = !online
    ? "Vos ventes sont enregistrées sur l'appareil et seront synchronisées automatiquement dès le retour du réseau."
    : errorCount > 0
      ? "Certaines opérations n'ont pas pu être envoyées. Réessayez la synchronisation."
      : "Des opérations attendent d'être envoyées au serveur.";

  return (
    <div className="px-4 md:px-6 py-3 text-white border-b border-white/10 shadow-md"
      style={{ background: "linear-gradient(160deg,#1A3A5C 0%,#16324F 55%,#0E2135 100%)" }}
      data-testid="sync-status-bar">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4">
        <div className="flex items-start gap-3 min-w-0 sm:flex-1">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-white/10 shrink-0">
            {!online ? <WifiOff className="w-5 h-5 text-[#93C5FD]" /> : errorCount > 0 ? <AlertTriangle className="w-5 h-5 text-[#FCA5A5]" /> : <Clock className="w-5 h-5 text-[#FCD34D]" />}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold font-head text-sm md:text-base leading-tight" data-testid="sync-online-state">{title}</span>
              {online && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-[#86EFAC]"><Wifi className="w-3 h-3" /> Reconnecté</span>}
            </div>
            <div className="text-white/70 text-xs md:text-sm leading-snug mt-0.5">{subtitle}</div>
          </div>
        </div>

        {/* Compteurs + actions : rangée qui s'enroule proprement sur mobile */}
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:shrink-0 sm:ml-auto">
          {pendingCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 px-3 py-1.5 text-sm font-bold shrink-0" data-testid="sync-pending-badge">
              <span className="tabular-nums text-base" data-testid="sync-pending-count">{pendingCount}</span>
              <span className="text-white/70 font-medium text-xs whitespace-nowrap">en attente</span>
            </span>
          )}
          {errorCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#B91C1C] px-3 py-1.5 text-sm font-bold shrink-0" data-testid="sync-error-badge">
              <span className="tabular-nums text-base" data-testid="sync-error-count">{errorCount}</span>
              <span className="text-white/80 font-medium text-xs whitespace-nowrap">en erreur</span>
            </span>
          )}
          {online && (pendingCount > 0 || errorCount > 0) && (
            <button
              className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-bold text-[#0E2135] shadow-sm hover:bg-slate-100 disabled:opacity-60 transition-colors shrink-0 whitespace-nowrap"
              onClick={() => flush()} disabled={syncing} data-testid="sync-now-btn">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Synchronisation…" : "Synchroniser"}
            </button>
          )}
          {queue.length > 0 && (
            <button className="inline-flex items-center gap-1 rounded-lg border border-white/25 px-3 py-2 text-sm font-medium text-white/90 hover:bg-white/10 transition-colors shrink-0 whitespace-nowrap" onClick={() => setOpenList((v) => !v)} data-testid="sync-toggle-list">
              Détails <ChevronDown className={`w-4 h-4 transition-transform ${openList ? "rotate-180" : ""}`} />
            </button>
          )}
          <button className="inline-flex items-center gap-1.5 rounded-lg border border-white/25 px-3 py-2 text-sm font-medium text-white/90 hover:bg-white/10 transition-colors shrink-0 whitespace-nowrap" onClick={() => setPinModal(true)} data-testid="pin-setup-btn" title="Code PIN hors-ligne">
            <KeyRound className="w-4 h-4" /> {pinSet ? "PIN ✓" : "PIN"}
          </button>
        </div>
      </div>

      {hasActions && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <ActionRow dark />
        </div>
      )}

      {openList && (
        <div className="mt-3 rounded-xl bg-black/15 p-3 space-y-1.5" data-testid="sync-op-list">
          {queue.length === 0 && <div className="text-white/60 text-xs">Aucune opération.</div>}
          {[...errors, ...pending, ...synced].map((op) => (
            <div key={op.uid} className="flex items-center gap-2 text-xs" data-testid={`sync-op-${op.uid}`}>
              {op.status === "synced" ? <CheckCircle2 className="w-3.5 h-3.5 text-[#86EFAC] shrink-0" /> : op.status === "error" ? <AlertTriangle className="w-3.5 h-3.5 text-[#FCA5A5] shrink-0" /> : <Clock className="w-3.5 h-3.5 text-[#FCD34D] shrink-0" />}
              <span className="font-mono text-white/90">{op.localSale?.number || op.type}</span>
              {op.localSale?.total != null && <span className="text-white/60">{money(op.localSale.total)}</span>}
              {op.status === "error" && <span className="text-[#FCA5A5] truncate">{op.error}</span>}
              {op.status === "error" && online && <button className="text-white underline underline-offset-2" onClick={() => retryOp(op.uid)} data-testid={`sync-retry-${op.uid}`}>Réessayer</button>}
            </div>
          ))}
          {synced.length > 0 && <button className="text-xs text-white/70 underline underline-offset-2" onClick={clearSynced} data-testid="sync-clear-synced">Effacer les synchronisées</button>}
        </div>
      )}
      {PinModal}
    </div>
  );
}
